import React, { useEffect, useState } from 'react';
import { X, MapPin, Phone, Mail, Clock, Star, ExternalLink, Info, Wifi, Cigarette, Car, MessageCircle } from 'lucide-react';
import { apiService } from '@/services/api';

interface Props {
  branchId: string;
  branchName?: string;
  onClose: () => void;
}

interface About {
  name?: string;
  description?: string | null;
  fullAddress?: string | null;
  mapQuery?: string | null;
  phone?: string | null;
  email?: string | null;
  whatsappNumber?: string | null;
  googleReviewUrl?: string | null;
  bannerImage?: string | null;
  operatingHours?: Record<string, { open?: string; close?: string }> | null;
  venueInfo?: Record<string, string> | null;
  branchType?: { code?: string; label?: string } | null;
  organizerName?: string | null;
}

const DAYS: [string, string][] = [
  ['monday', 'Mon'], ['tuesday', 'Tue'], ['wednesday', 'Wed'], ['thursday', 'Thu'],
  ['friday', 'Fri'], ['saturday', 'Sat'], ['sunday', 'Sun'],
];

// Which venueInfo facts to surface, with an icon + friendly label.
const FACTS: { key: string; label: string; icon: React.ReactNode }[] = [
  { key: 'happyHour', label: 'Happy hour', icon: <Clock size={15} color="#e0531f" /> },
  { key: 'wifi', label: 'Wi-Fi', icon: <Wifi size={15} color="#e0531f" /> },
  { key: 'parking', label: 'Parking', icon: <Car size={15} color="#e0531f" /> },
  { key: 'smoking', label: 'Smoking', icon: <Cigarette size={15} color="#e0531f" /> },
  { key: 'facilities', label: 'Facilities', icon: <Info size={15} color="#e0531f" /> },
  { key: 'rules', label: 'House rules', icon: <Info size={15} color="#e0531f" /> },
];

const BranchAbout: React.FC<Props> = ({ branchId, branchName, onClose }) => {
  const [about, setAbout] = useState<About | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService.getBranchAbout(branchId)
      .then((res: any) => { if (!cancelled) setAbout(res?.about || res?.data?.about || null); })
      .catch(() => { if (!cancelled) setError('Could not load venue details.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [branchId]);

  const name = about?.name || branchName || 'This venue';
  const facts = FACTS.filter((f) => about?.venueInfo && (about.venueInfo as any)[f.key]);
  const hoursNote = about?.venueInfo?.hours;
  const hasStructuredHours = about?.operatingHours && Object.values(about.operatingHours).some((h) => h && (h.open || h.close));

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-label="About this venue">
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        {/* Header — banner if available, else a simple bar. */}
        {about?.bannerImage ? (
          <div style={{ ...bannerWrap, backgroundImage: `url(${about.bannerImage})` }}>
            <button type="button" onClick={onClose} style={closeOnBanner} aria-label="Close"><X size={20} /></button>
            <div style={bannerScrim}>
              <div style={bannerName}>{name}</div>
              {about?.branchType?.label && <div style={bannerType}>{about.branchType.label}</div>}
            </div>
          </div>
        ) : (
          <div style={header}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>{name}</div>
              {about?.branchType?.label && <div style={subLabel}>{about.branchType.label}</div>}
            </div>
            <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={20} /></button>
          </div>
        )}

        <div style={body}>
          {loading ? (
            <p style={muted}>Loading venue details…</p>
          ) : error ? (
            <p style={muted}>{error}</p>
          ) : (
            <>
              {about?.description && <p style={desc}>{about.description}</p>}

              {about?.fullAddress && (
                <a
                  style={rowLink}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(about.mapQuery || about.fullAddress)}`}
                  target="_blank" rel="noopener noreferrer"
                >
                  <span style={rowIcon}><MapPin size={18} color="#e0531f" /></span>
                  <span style={{ flex: 1 }}>
                    <span style={rowTitle}>{about.fullAddress}</span>
                    <span style={rowSub}>Get directions</span>
                  </span>
                  <ExternalLink size={15} color="#9ca3af" />
                </a>
              )}

              {about?.phone && (
                <a style={rowLink} href={`tel:${about.phone}`}>
                  <span style={rowIcon}><Phone size={18} color="#e0531f" /></span>
                  <span style={{ flex: 1 }}><span style={rowTitle}>{about.phone}</span><span style={rowSub}>Call the venue</span></span>
                </a>
              )}

              {about?.whatsappNumber && (
                <a style={{ ...rowLink, borderColor: '#25D36633', background: '#e8faf0' }} href={`https://wa.me/${String(about.whatsappNumber).replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                  <span style={{ ...rowIcon, background: '#d6f5e3' }}><MessageCircle size={18} color="#128C4B" /></span>
                  <span style={{ flex: 1 }}><span style={rowTitle}>Chat on WhatsApp</span><span style={rowSub}>Message the venue</span></span>
                  <ExternalLink size={15} color="#9ca3af" />
                </a>
              )}

              {about?.email && (
                <a style={rowLink} href={`mailto:${about.email}`}>
                  <span style={rowIcon}><Mail size={18} color="#e0531f" /></span>
                  <span style={{ flex: 1 }}><span style={rowTitle}>{about.email}</span><span style={rowSub}>Email the venue</span></span>
                </a>
              )}

              {(hasStructuredHours || hoursNote) && (
                <div style={block}>
                  <div style={blockHead}><Clock size={15} color="#e0531f" /> Opening hours</div>
                  {hasStructuredHours ? (
                    <div>
                      {DAYS.map(([key, label]) => {
                        const h = about?.operatingHours?.[key];
                        const val = h && (h.open || h.close) ? `${h.open || '—'} – ${h.close || '—'}` : 'Closed';
                        return (
                          <div key={key} style={hoursRow}><span style={{ color: '#6b7280' }}>{label}</span><span style={{ fontWeight: 600 }}>{val}</span></div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ ...desc, margin: 0 }}>{hoursNote}</p>
                  )}
                </div>
              )}

              {facts.length > 0 && (
                <div style={block}>
                  <div style={blockHead}><Info size={15} color="#e0531f" /> Good to know</div>
                  {facts.map((f) => (
                    <div key={f.key} style={factRow}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#374151', minWidth: 110 }}>{f.icon}{f.label}</span>
                      <span style={{ flex: 1, color: '#111827' }}>{(about?.venueInfo as any)?.[f.key]}</span>
                    </div>
                  ))}
                  {about?.venueInfo?.note && <p style={{ ...desc, marginTop: 8, marginBottom: 0 }}>{about.venueInfo.note}</p>}
                </div>
              )}

              {about?.googleReviewUrl && (
                <a style={reviewBtn} href={about.googleReviewUrl} target="_blank" rel="noopener noreferrer">
                  <Star size={16} /> Leave a review
                </a>
              )}

              {!about?.description && !about?.fullAddress && !about?.phone && !about?.email && !hasStructuredHours && !hoursNote && facts.length === 0 && (
                <p style={muted}>This venue hasn’t added details yet.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' };
const sheet: React.CSSProperties = { width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, color: '#111827' };
const bannerWrap: React.CSSProperties = { position: 'relative', height: 160, backgroundSize: 'cover', backgroundPosition: 'center', borderTopLeftRadius: 20, borderTopRightRadius: 20 };
const bannerScrim: React.CSSProperties = { position: 'absolute', left: 0, right: 0, bottom: 0, padding: '28px 16px 12px', background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)', color: '#fff', borderBottomLeftRadius: 0 };
const bannerName: React.CSSProperties = { fontWeight: 800, fontSize: '1.2rem' };
const bannerType: React.CSSProperties = { fontSize: '0.82rem', opacity: 0.9 };
const closeOnBanner: React.CSSProperties = { position: 'absolute', top: 10, right: 10, width: 36, height: 36, borderRadius: 999, border: 'none', background: 'rgba(0,0,0,0.4)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 8px' };
const subLabel: React.CSSProperties = { fontSize: '0.82rem', color: '#6b7280' };
const iconBtn: React.CSSProperties = { width: 36, height: 36, borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const body: React.CSSProperties = { padding: 16, display: 'flex', flexDirection: 'column', gap: 12 };
const muted: React.CSSProperties = { color: '#6b7280', fontSize: '0.9rem', textAlign: 'center', padding: '1rem 0' };
const desc: React.CSSProperties = { fontSize: '0.92rem', lineHeight: 1.5, color: '#374151', margin: 0 };
const rowLink: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, border: '1px solid #f0e6e0', background: '#fff7f4', textDecoration: 'none', color: '#111827' };
const rowIcon: React.CSSProperties = { width: 34, height: 34, borderRadius: 10, background: '#ffe9e0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const rowTitle: React.CSSProperties = { display: 'block', fontWeight: 600, fontSize: '0.9rem' };
const rowSub: React.CSSProperties = { display: 'block', fontSize: '0.76rem', color: '#9a6a54' };
const block: React.CSSProperties = { border: '1px solid #eef0f2', borderRadius: 12, padding: 12 };
const blockHead: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: '0.88rem', marginBottom: 8 };
const hoursRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: '0.86rem', padding: '3px 0' };
const factRow: React.CSSProperties = { display: 'flex', gap: 8, fontSize: '0.86rem', padding: '4px 0', alignItems: 'flex-start' };
const reviewBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', borderRadius: 12, background: '#e0531f', color: '#fff', fontWeight: 700, textDecoration: 'none' };

export default BranchAbout;
